import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Record } from 'immutable';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';

// Selectors
import { getWorkspaceDisplayLabel } from 'Shared/selectors/workspace';

// Actions
import * as MeetingItemActions from 'Shared/actions/MeetingItems.actions';

// Components
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import WpSelect from 'Wpds/components/select/WpSelectV2';
import WpCheckBox from 'Wpds/components/checkbox/WpCheckbox';
import { WpFormLayout } from 'Wpds/components/form';
import WpBadge from 'Wpds/components/badge/WpBadge';
import WpLabelled from 'Wpds/components/labelled/WpLabelled';

// Styles
import './CopyMoveActionItemModal.scss';

type Props = {
  isOpen: boolean;
  toggleOpen: () => void;
  handleCloseDetailsPanel?: Function;
  meetingItem: Record<MeetingItem>;
};

const CopyMoveActionItemModal = ({ isOpen, toggleOpen, handleCloseDetailsPanel, meetingItem }: Props) => {
  const dispatch = useDispatch();
  const [isMakeCopy, toggleIsMakeCopy] = useToggle(false);
  const [isSaving, toggleIsSaving] = useToggle(false);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState([]);

  const uuid = MeetingItemHelpers.getUuid(meetingItem);
  const workspaceId = MeetingItemHelpers.getWorkspaceId(meetingItem);

  const workspaces = useSelector(state =>
    state.workspaces.map(workspace => ({
      ...workspace,
      value: workspace.id,
      label: getWorkspaceDisplayLabel(workspace.id)(state),
    }))
  );

  const filteredWorkspaces = workspaces.filter(workspace => !selectedWorkspaces.includes(workspace.value));

  const handleMoveClick = () => {
    toggleIsSaving();
    const data = {
      itemUuids: [uuid],
      duplicateFromWorkspaceId: workspaceId,
      duplicateToWorkspaceIds: selectedWorkspaces,
      isMoveItems: !isMakeCopy,
    };
    dispatch(MeetingItemActions.duplicateItems(data))
      .then(() => {
        toggleIsSaving();
        toggleOpen();
        if (handleCloseDetailsPanel) handleCloseDetailsPanel();
      })
      .catch(() => {
        toggleIsSaving();
      });
  };

  const handleSelectWorkspace = selected => {
    if (isMakeCopy) setSelectedWorkspaces([...selectedWorkspaces, selected]);
    else setSelectedWorkspaces([selected]);
  };

  const handleCopyToCheckboxToggle = () => {
    if (isMakeCopy) {
      setSelectedWorkspaces([]);
    }
    toggleIsMakeCopy();
  };

  useEffect(() => {
    setSelectedWorkspaces([]);
  }, [isOpen]);

  const isDisabled = selectedWorkspaces.length === 0;
  const showCopyToList = isMakeCopy && selectedWorkspaces.length > 0;
  const baseClassName = 'CopyMoveActionItemModal';

  const getWorkspaceName = uuid => {
    return workspaces.find(ws => uuid === ws.value).label;
  };

  const handleRemoveWorkspaceFromList = uuid => {
    setSelectedWorkspaces(selectedWorkspaces.filter(id => id !== uuid));
  };

  if (!isOpen) return null;
  return (
    <WpModal isOpen={isOpen} size="sm" onToggle={toggleOpen} dismissOnBackdrop={false}>
      <WpModalHeader onToggle={toggleOpen}>Move To Another Workspace</WpModalHeader>
      <WpModalBody>
        <p>Choose the workspace you would like to move this item to.</p>
        <WpFormLayout>
          <WpSelect
            id="workspaces-select"
            label="Select a workspace"
            labelHidden
            options={filteredWorkspaces}
            placeholder="Choose workspace"
            onChange={handleSelectWorkspace}
            isSearchable
            searchPlaceholder="Search workspaces"
            resetOnSelect={isMakeCopy}
          />
          <WpCheckBox
            checked={isMakeCopy}
            id="copy-to-workspace"
            label="Make a copy"
            descriptionText="Copy this item to one or many workspaces."
            onChange={handleCopyToCheckboxToggle}
          />
          {showCopyToList && (
            <WpLabelled id="list-of-copy-to-workspaces" label="Copy to workspaces:" showRequiredIndicator={false}>
              <div className={`${baseClassName}__workspace-badge-list`}>
                {selectedWorkspaces.map(workspaceUuid => (
                  <p className={`${baseClassName}__workspace-badge-list__item`} key={workspaceUuid}>
                    <WpBadge size="lg" onDismiss={() => handleRemoveWorkspaceFromList(workspaceUuid)}>
                      {getWorkspaceName(workspaceUuid)}
                    </WpBadge>
                  </p>
                ))}
              </div>
            </WpLabelled>
          )}
        </WpFormLayout>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={toggleOpen} disabled={isSaving}>
            Cancel
          </WpButton>
          <WpButton onClick={handleMoveClick} disabled={isDisabled} loading={isSaving}>
            {isMakeCopy ? 'Copy' : 'Move'} Item
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default CopyMoveActionItemModal;
